import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import IconBtn from "@mirage/components/buttons/IconBtn";
import { MapPrint, PrintMapRequest } from "@shared/mirage/services/IEarthlightService";
import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import { useCurrentApplicationStore } from "@mirage/store/store";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { useGaiaMapContext } from "../hooks/useGaiaMapContext";
import { useConfirmDialog } from "@mirage/hooks/useConfirmDialog";
import Field from "@mirage/components/regularField/Field";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import useAlertService from "@mirage/hooks/useAlertService";

interface GaiaMapPrintPreviewProps {
    isActive: boolean;
    onClose: () => void;
    map: L.Map | null;
    onSnapshotRequested(printRequest: PrintMapRequest): Promise<void>;
}

interface PrintNameInputProps {
    onValueChange: (value: string) => void;
    initialValue: string;
}

const PrintNameInput = ({ onValueChange, initialValue }: PrintNameInputProps) => {
    const [printName, setPrintName] = useState(initialValue ?? "");
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = useCallback(
        (e: any, v: string) => {
            setPrintName(v);
            onValueChange(v);
            if (!v) {
                setErrorMsg("is required");
            } else {
                setErrorMsg("");
            }
        },
        [onValueChange]
    );

    return <Field label="Snapshot Attachment Name" mode="edit" contentType="TEXT" value={printName} onChange={handleChange} errorMsg={errorMsg} isRequired={true} />;
};

const SCALE_OPTIONS = [
    { value: 500, label: "1 : 500" },
    { value: 1250, label: "1 : 1250" },
    { value: 2500, label: "1 : 2500" },
    { value: 5000, label: "1 : 5000" },
    { value: 7500, label: "1 : 7500" },
    { value: 10000, label: "1 : 10000" },
] as const;

export const GaiaMapPrintPreview = memo(({ isActive, onClose, map, onSnapshotRequested }: GaiaMapPrintPreviewProps) => {
    const currentAppName = useCurrentApplicationStore((state) => state.currentApplication);
    const [sessionValue] = useSessionStorage(currentAppName);
    const { clientSettings } = sessionValue;
    const printMapScaleOptions = clientSettings?.PRINT_MAP_SCALE_OPTIONS;
    const scaleOptions = Array.isArray(printMapScaleOptions) ? printMapScaleOptions : SCALE_OPTIONS;
    const defaultScale = SCALE_OPTIONS[0] || { value: 1250, label: "1 : 1250" };
    const [selectedScale, setSelectedScale] = useState<number>(defaultScale?.value || 1250);
    const selectedScaleRef = useRef<number>(selectedScale);
    const { startPrintLoading, stopPrintLoading, mapBoxSize } = useGaiaMapContext();
    const printOverlayRef = useRef<HTMLDivElement | null>(null);
    const printControlsRef = useRef<HTMLDivElement | null>(null);
    const mapBoxHeightInMeters = mapBoxSize != null && "heightInMeters" in mapBoxSize ? mapBoxSize.heightInMeters : null;
    const mapBoxWidthInMeters = mapBoxSize != null && "widthInMeters" in mapBoxSize ? mapBoxSize.widthInMeters : null;
    const [showConfirmDialog, _, changeState] = useConfirmDialog();
    const showAlert = useAlertService();

    const updatePrintOverlay = useCallback(() => {
        if (!map || !printOverlayRef.current) {
            return;
        }

        const mapContainer = map.getContainer();
        const mapW = mapContainer.offsetWidth;
        const mapH = mapContainer.offsetHeight;
        if (!mapBoxHeightInMeters || !mapBoxWidthInMeters) {
            showAlert("Paper width or height not found", "error");
            return;
        }
        const paperWm = mapBoxWidthInMeters;
        const paperHm = mapBoxHeightInMeters;
        const realWm = paperWm * selectedScaleRef.current;
        const realHm = paperHm * selectedScaleRef.current;

        const center = map.getCenter();

        const halfWidthMeters = realWm / 2;
        const halfHeightMeters = realHm / 2;

        const metersPerDegreeLat = 111320;
        const metersPerDegreeLng = 111320 * Math.cos((center.lat * Math.PI) / 180);

        const halfWidthDeg = halfWidthMeters / metersPerDegreeLng;
        const halfHeightDeg = halfHeightMeters / metersPerDegreeLat;

        const southWest = L.latLng(center.lat - halfHeightDeg, center.lng - halfWidthDeg);
        const northEast = L.latLng(center.lat + halfHeightDeg, center.lng + halfWidthDeg);

        const swPixel = map.latLngToContainerPoint(southWest);
        const nePixel = map.latLngToContainerPoint(northEast);

        const boxWpx = Math.abs(nePixel.x - swPixel.x);
        const boxHpx = Math.abs(nePixel.y - swPixel.y);

        const finalWpx = boxWpx;
        const finalHpx = boxHpx;

        const left = (mapW - finalWpx) / 2;
        const top = (mapH - finalHpx) / 2;

        printOverlayRef.current.style.left = `${left}px`;
        printOverlayRef.current.style.top = `${top}px`;
        printOverlayRef.current.style.width = `${finalWpx}px`;
        printOverlayRef.current.style.height = `${finalHpx}px`;

        if (printControlsRef.current) {
            printControlsRef.current.style.left = `${left}px`;
            printControlsRef.current.style.top = `${top - 32}px`;
        }
    }, [map, mapBoxHeightInMeters, mapBoxWidthInMeters, showAlert]);

    const handleScaleChange = useCallback((newScale: number) => {
        setSelectedScale(newScale);
        selectedScaleRef.current = newScale;
    }, []);

    const handlePrint = useCallback(async () => {
        if (!map || !printOverlayRef.current) {
            return;
        }

        const center = map.getCenter();

        const geoJsonMapCenter = { type: "Point", coordinates: [center.lng, center.lat] } as GeoJSON.Point;
        const mapCenterFeatureCollection: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: [{ type: "Feature", geometry: geoJsonMapCenter, properties: {} }],
        };
        const projected = GeometryProjector.projectTo27700(mapCenterFeatureCollection) as any;
        const [mapCenterX, mapCenterY] = projected.features[0].geometry.coordinates;

        const mapPrint: MapPrint = {
            scale: selectedScale,
            centerX: mapCenterX,
            centerY: mapCenterY,
        };

        const request = {
            map: {
                scale: selectedScale,
                centerX: mapCenterX,
                centerY: mapCenterY,
            },
            prints: [mapPrint],
            appName: currentAppName,
            printName: "",
        } as PrintMapRequest;
        onClose();
        const timeStamp = DateFormatter.formatToDate(new Date(), "YYYYMMDDHHmmss");
        const defaultPrintName = "MapSnapshot";
        let currentPrintName = `${defaultPrintName}_${timeStamp}`;
        const nameUpdate = (
            <PrintNameInput
                initialValue={currentPrintName}
                onValueChange={(value) => {
                    if (!value) {
                        changeState({ onConfirmDisabled: true });
                    } else {
                        changeState({ onConfirmDisabled: false });
                    }
                    currentPrintName = value;
                }}
            />
        );

        const confirmed = await showConfirmDialog({
            header: "Take Snapshot",
            mutation: nameUpdate,
            onConfirmLabel: "Print",
            onRejectLabel: "Cancel",
        });
        if (!confirmed) {
            return;
        }
        request.printName = currentPrintName;
        startPrintLoading();
        await onSnapshotRequested(request);
        stopPrintLoading();
    }, [map, selectedScale, currentAppName, onClose, showConfirmDialog, startPrintLoading, onSnapshotRequested, stopPrintLoading, changeState]);

    useEffect(() => {
        if (!map) {
            return;
        }
        const handleMapUpdate = () => {
            updatePrintOverlay();
        };

        if (!isActive) {
            map.off("move", handleMapUpdate);
            map.off("zoom", handleMapUpdate);
            map.off("resize", handleMapUpdate);
            return;
        }

        map.on("move", handleMapUpdate);
        map.on("zoom", handleMapUpdate);
        map.on("resize", handleMapUpdate);

        updatePrintOverlay();

        return () => {
            map.off("move", handleMapUpdate);
            map.off("zoom", handleMapUpdate);
            map.off("resize", handleMapUpdate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isActive]);

    useEffect(() => {
        if (isActive) {
            updatePrintOverlay();
        }
    }, [selectedScale, isActive, updatePrintOverlay]);

    if (!isActive || !map) {
        return null;
    }

    return (
        <>
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    pointerEvents: "none",
                    zIndex: 1000,
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        pointerEvents: "none",
                    }}
                >
                    <div
                        ref={printOverlayRef}
                        style={{
                            position: "absolute",
                            border: "2px solid #007ACC",
                            backgroundColor: "transparent",
                            pointerEvents: "none",
                            boxSizing: "border-box",
                        }}
                    />
                </div>

                <div
                    ref={printControlsRef}
                    style={{
                        position: "absolute",
                        background: "white",
                        padding: "2px 4px",
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontFamily: "inherit",
                        zIndex: 1,
                        pointerEvents: "auto",
                    }}
                >
                    <select
                        value={selectedScale}
                        onChange={(e) => handleScaleChange(Number(e.target.value))}
                        style={{
                            padding: "0",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            backgroundColor: "white",
                            cursor: "pointer",
                            outline: "none",
                            minWidth: "100px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = "#1976d2";
                            e.target.style.boxShadow = "0 0 0 2px rgba(25, 118, 210, 0.2)";
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = "#ccc";
                            e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                        }}
                    >
                        {scaleOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={handlePrint}
                        style={{
                            padding: "2px 6px",
                            backgroundColor: "#1976d2",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#1565c0";
                            e.currentTarget.style.boxShadow = "0 3px 6px rgba(0,0,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#1976d2";
                            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
                        }}
                        onMouseDown={(e) => {
                            e.currentTarget.style.backgroundColor = "#0d47a1";
                        }}
                        onMouseUp={(e) => {
                            e.currentTarget.style.backgroundColor = "#1565c0";
                        }}
                    >
                        Print
                    </button>
                    <IconBtn icon={faXmark} onClick={onClose} title="Close" height={12} width={12} />
                </div>
            </div>
        </>
    );
});
